import { useModalQueue } from '@carvana/merch-react-hooks';
import deepmerge from 'deepmerge';
import React, { createContext, useMemo } from 'react';
import type { DeepPartial } from '~/types';
import { ModalId, ModalPriorities } from './constants';

type Toggler = (isOpen: boolean) => void;
type ContextType = {
  isAddCoApplicantModalOpen: boolean;
  isAnyModalOpen: boolean;
  isEditCoApplicantModalOpen: boolean;
  isSoftPullModalOpen: boolean;
  setAddCoApplicantModalOpen: Toggler;
  setEditCoApplicantModalOpen: Toggler;
  setSoftPullModalOpen: Toggler;
};

export const defaultContext: ContextType = {
  isAddCoApplicantModalOpen: false,
  isAnyModalOpen: false,
  isEditCoApplicantModalOpen: false,
  isSoftPullModalOpen: false,
  setAddCoApplicantModalOpen() {},
  setEditCoApplicantModalOpen() {},
  setSoftPullModalOpen() {}
};

const ModalContext = createContext<ContextType>(defaultContext);

export function ModalContextProvider({ children }: React.PropsWithChildren) {
  const [currentModalId, generateOpener] = useModalQueue(ModalPriorities);

  const {
    setAddCoApplicantModalOpen,
    setEditCoApplicantModalOpen,
    setSoftPullModalOpen
  } = useMemo(
    () => ({
      setAddCoApplicantModalOpen: generateOpener(ModalId.AddCoApplicant),
      setEditCoApplicantModalOpen: generateOpener(ModalId.EditCoApplicant),
      setSoftPullModalOpen: generateOpener(ModalId.SoftPull)
    }),
    [generateOpener]
  );

  const value = useMemo<ContextType>(
    (): ContextType => ({
      isAddCoApplicantModalOpen: currentModalId === ModalId.AddCoApplicant,
      isAnyModalOpen: currentModalId > -1,
      isEditCoApplicantModalOpen: currentModalId === ModalId.EditCoApplicant,
      isSoftPullModalOpen: currentModalId === ModalId.SoftPull,
      setAddCoApplicantModalOpen,
      setEditCoApplicantModalOpen,
      setSoftPullModalOpen
    }),
    [
      currentModalId,
      setAddCoApplicantModalOpen,
      setEditCoApplicantModalOpen,
      setSoftPullModalOpen
    ]
  );

  return (
    <ModalContext.Provider value={value}>{children}</ModalContext.Provider>
  );
}

ModalContextProvider.displayName = 'ModalContextProvider';

export default ModalContext;

export const generateModalContext = (
  customState: DeepPartial<ContextType>
): ContextType =>
  deepmerge<ContextType, DeepPartial<ContextType>>(defaultContext, customState);

export function useModalContext() {
  return React.useContext(ModalContext);
}
